(function () {
  'use strict';

  const COMPONENT_NAME = 'rcc-product-badges';
  const API_VERSION = '2026-01';
  const BADGE_CONFIG_ELEMENT_ID = 'rcc-product-badges-config';
  const NEW_BADGE_SYSTEM_ID = '208947085576';
  const DEFAULT_BADGE_CONFIG = Object.freeze({
    soldOutLabel: 'Sold out',
  });
  const badgeCache = new Map();
  const pendingRequests = new Map();
  let themeBadgeConfig = null;

  class ProductBadgesComponent extends HTMLElement {
    static get observedAttributes() {
      return ['product'];
    }

    constructor() {
      super();
      this._product = null;
      this._renderVersion = 0;
    }

    get product() {
      return this._product;
    }

    set product(value) {
      this._product = value;
      this.render();
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name !== 'product' || oldValue === newValue || !newValue) return;

      try {
        this.product = JSON.parse(newValue);
      } catch (_error) {
        this.render();
      }
    }

    connectedCallback() {
      if (this._product || this.hasAttribute('product')) {
        this.render();
      }
    }

    getSerializedProduct() {
      const productAttribute = this.getAttribute('product');
      if (!productAttribute) return null;

      try {
        return JSON.parse(productAttribute);
      } catch (_error) {
        return null;
      }
    }

    async render() {
      const renderVersion = ++this._renderVersion;
      const product = this._product || this.getSerializedProduct();

      if (!product) {
        this.innerHTML = '';
        this.hidden = true;
        return;
      }

      const directBadges = getArrayMetafield(product, 'wardow', 'badges');
      const storefrontProduct = needsStorefrontProduct(product, directBadges) ? await this.getStorefrontProduct(product) : null;
      if (renderVersion !== this._renderVersion) return;

      const badges = this.getBadges(product, storefrontProduct, directBadges);

      if (badges.length === 0) {
        this.innerHTML = '';
        this.hidden = true;
        return;
      }

      this.hidden = false;
      this.innerHTML = `
        <div class="rcc-product-badges" aria-label="Product labels">
          ${badges.map((badge) => renderBadgeMarkup(badge)).join('')}
        </div>
      `;
    }

    async getStorefrontProduct(product) {
      const requestConfig = resolveProductRequest(product, getStorefrontContext(), 'badge');
      if (!requestConfig) return null;

      if (badgeCache.has(requestConfig.cacheKey)) {
        return badgeCache.get(requestConfig.cacheKey);
      }

      if (pendingRequests.has(requestConfig.cacheKey)) {
        return pendingRequests.get(requestConfig.cacheKey);
      }

      const token = getStorefrontToken(this);
      if (!token) return null;

      const request = fetch(`/api/${API_VERSION}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({
          query: requestConfig.query,
          variables: requestConfig.variables,
        }),
      })
        .then((response) => response.json())
        .then((payload) => {
          if (payload.errors) {
            console.error('Retail Cloud Connect badge component query failed.', payload.errors);
            return null;
          }

          const storefrontProduct = requestConfig.extractProduct(payload);
          badgeCache.set(requestConfig.cacheKey, storefrontProduct);
          return storefrontProduct;
        })
        .catch((error) => {
          console.error('Retail Cloud Connect badge component fetch failed.', error);
          return null;
        })
        .finally(() => {
          pendingRequests.delete(requestConfig.cacheKey);
        });

      pendingRequests.set(requestConfig.cacheKey, request);
      return request;
    }

    getBadges(product, storefrontProduct, directBadges) {
      const badgeState = resolveBadgeState(product, storefrontProduct);
      const fetchedBadges = storefrontProduct?.badges?.references?.nodes ?? [];
      const specialBadge = buildSpecialBadge(badgeState, getThemeBadgeConfig());

      const badges = (directBadges.length > 0 ? directBadges : fetchedBadges)
        .map(normalizeBadge)
        .filter((badge) => badge.active !== false && badge.label);

      const visibleBadges = badges.filter((badge) => !isSuppressedNewBadge(badge, badgeState.hasSalePriceForSuppression));

      return specialBadge ? [specialBadge, ...visibleBadges] : visibleBadges;
    }
  }

  function resolveProductRequest(product, context, intent) {
    const selection = getProductSelection(intent);
    const contextDirective = `@inContext(country: ${context.country}, language: ${context.language})`;
    const handle = resolveProductHandle(product);

    if (handle) {
      return {
        cacheKey: `handle:${handle}`,
        variables: { handle },
        query: `
          query ProductBadgeMetaByHandle($handle: String!) ${contextDirective} {
            product(handle: $handle) {
              ${selection}
            }
          }
        `,
        extractProduct: (payload) => payload.data?.product ?? null,
      };
    }

    const productId = resolveProductGid(product);
    if (productId) {
      return {
        cacheKey: `product:${productId}`,
        variables: { id: productId },
        query: `
          query ProductBadgeMetaById($id: ID!) ${contextDirective} {
            node(id: $id) {
              ... on Product {
                ${selection}
              }
            }
          }
        `,
        extractProduct: (payload) => payload.data?.node ?? null,
      };
    }

    const variantId = resolveVariantGid(product);
    if (!variantId) return null;

    return {
      cacheKey: `variant:${variantId}`,
      variables: { id: variantId },
      query: `
        query ProductBadgeMetaByVariant($id: ID!) ${contextDirective} {
          node(id: $id) {
            ... on ProductVariant {
              product {
                ${selection}
              }
            }
          }
        }
      `,
      extractProduct: (payload) => payload.data?.node?.product ?? null,
    };
  }

  function getProductSelection(intent) {
    if (intent !== 'badge') return '';

    return `
      id
      handle
      availableForSale
      priceRange {
        minVariantPrice {
          amount
        }
      }
      compareAtPriceRange {
        maxVariantPrice {
          amount
        }
      }
      badges: metafield(namespace: "wardow", key: "badges") {
        references(first: 6) {
          nodes {
            ... on Metaobject {
              id
              fields {
                key
                value
              }
            }
          }
        }
      }
    `;
  }

  function resolveProductHandle(product) {
    const handle = readString(product?.handle) || readString(product?.product?.handle);
    if (handle) return handle;

    const url = readString(product?.onlineStoreUrl) || readString(product?.url) || readString(product?.product?.url);
    if (!url) return '';

    const match = url.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  function resolveProductGid(product) {
    return normalizeGid(
      product?.id || product?.productId || product?.product?.id || product?.product?.productId,
      'Product'
    );
  }

  function resolveVariantGid(product) {
    return normalizeGid(
      product?.variantId ||
        product?.selectedOrFirstAvailableVariantId ||
        product?.selectedVariantId ||
        product?.variant?.id,
      'ProductVariant'
    );
  }

  function normalizeGid(value, resourceType) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return `gid://shopify/${resourceType}/${value}`;
    }

    const normalizedValue = String(value || '').trim();
    if (!normalizedValue) return '';

    const gidMatch = normalizedValue.match(/^gid:\/\/shopify\/([A-Za-z]+)\/(\d+)$/);
    if (gidMatch) {
      return gidMatch[1] === resourceType ? normalizedValue : '';
    }

    const resourceMatch = normalizedValue.match(/\/(Product|ProductVariant)\/(\d+)$/);
    if (resourceMatch) {
      return resourceMatch[1] === resourceType ? `gid://shopify/${resourceMatch[1]}/${resourceMatch[2]}` : '';
    }

    return /^\d+$/.test(normalizedValue) ? `gid://shopify/${resourceType}/${normalizedValue}` : '';
  }

  function getStorefrontToken(element) {
    return element.closest('.rcc-search')?.dataset.token || document.querySelector('.rcc-search')?.dataset.token || '';
  }

  function getStorefrontContext() {
    const htmlLanguage = document.documentElement.lang || 'de';
    const language = sanitizeEnum(window.Shopify?.locale || htmlLanguage.split('-')[0] || 'de', 'DE');
    const country = sanitizeEnum(window.Shopify?.country || htmlLanguage.split('-')[1] || language, 'DE');

    return { country, language };
  }

  function sanitizeEnum(value, fallback) {
    const normalized = String(value || '').trim().slice(0, 2).toUpperCase();
    return /^[A-Z]{2}$/.test(normalized) ? normalized : fallback;
  }

  function getArrayMetafield(product, namespace, key) {
    return normalizeMetafieldList(getMetafield(product, namespace, key));
  }

  function getMetafield(product, namespace, key) {
    const metafields = product?.metafields;
    if (!metafields) return null;

    if (metafields?.[namespace]?.[key] != null) {
      return metafields[namespace][key];
    }

    if (metafields?.[`${namespace}.${key}`] != null) {
      return metafields[`${namespace}.${key}`];
    }

    if (metafields?.[key] != null) {
      return metafields[key];
    }

    if (Array.isArray(metafields)) {
      return metafields.find((entry) => entry?.namespace === namespace && entry?.key === key) || null;
    }

    return null;
  }

  function normalizeMetafieldList(value, depth = 0) {
    if (!value || depth > 3) return [];

    if (Array.isArray(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = safeJsonParse(value);
      return parsed ? normalizeMetafieldList(parsed, depth + 1) : [];
    }

    if (Array.isArray(value.nodes)) {
      return value.nodes;
    }

    if (Array.isArray(value.edges)) {
      return value.edges.map((edge) => edge?.node).filter(Boolean);
    }

    if (value.references) {
      return normalizeMetafieldList(value.references, depth + 1);
    }

    if (value.value != null) {
      return normalizeMetafieldList(value.value, depth + 1);
    }

    if (Array.isArray(value.items)) {
      return value.items;
    }

    return [];
  }

  function needsStorefrontProduct(product, directBadges) {
    return directBadges.length === 0 || !hasLocalBadgeState(product);
  }

  function hasLocalBadgeState(product) {
    return (
      resolveLocalAvailability(product) !== null &&
      resolveDirectBadgePrice(product) !== null &&
      resolveDirectSuppressionCompareAtPrice(product) !== null
    );
  }

  function safeJsonParse(value) {
    try {
      return JSON.parse(value);
    } catch (_error) {
      return null;
    }
  }

  function resolveBadgeState(product, storefrontProduct) {
    const available = resolveAvailability(product, storefrontProduct);
    const price = resolveBadgePrice(product, storefrontProduct);
    const compareAtPrice = resolveBadgeCompareAtPrice(product, storefrontProduct);
    const suppressionCompareAtPrice = resolveSuppressionCompareAtPrice(product, storefrontProduct);
    const hasSalePrice = price > 0 && compareAtPrice > price;
    const hasSalePriceForSuppression = price > 0 && suppressionCompareAtPrice > price;

    return {
      available,
      hasSalePrice,
      hasSalePriceForSuppression,
      salePercentage: hasSalePrice ? calculateSalePercentage(price, compareAtPrice) : null,
    };
  }

  function resolveAvailability(product, storefrontProduct) {
    const localAvailability = resolveLocalAvailability(product);
    if (localAvailability !== null) return localAvailability;

    const storefrontAvailability = firstBoolean([storefrontProduct?.availableForSale, storefrontProduct?.available]);
    return storefrontAvailability !== null ? storefrontAvailability : true;
  }

  function resolveLocalAvailability(product) {
    return firstBoolean([
      product?.available,
      product?.availableForSale,
      product?.product?.available,
      product?.product?.availableForSale,
    ]);
  }

  function resolveBadgePrice(product, storefrontProduct) {
    return resolveDirectBadgePrice(product) ?? resolveStorefrontBadgePrice(storefrontProduct) ?? 0;
  }

  function resolveDirectBadgePrice(product) {
    return readFirstNumber([
      product?.price,
      product?.priceMin,
      product?.minPrice,
      product?.price_min,
      product?.priceRange?.minVariantPrice?.amount,
      product?.product?.price,
      product?.product?.priceMin,
      product?.product?.minPrice,
      product?.product?.price_min,
      product?.product?.priceRange?.minVariantPrice?.amount,
    ]);
  }

  function resolveStorefrontBadgePrice(storefrontProduct) {
    return readFirstNumber([storefrontProduct?.priceRange?.minVariantPrice?.amount]);
  }

  function resolveBadgeCompareAtPrice(product, storefrontProduct) {
    return (
      resolveDirectCurrentCompareAtPrice(product) ??
      resolveDirectSuppressionCompareAtPrice(product) ??
      resolveStorefrontSuppressionCompareAtPrice(storefrontProduct) ??
      0
    );
  }

  function resolveSuppressionCompareAtPrice(product, storefrontProduct) {
    return resolveDirectSuppressionCompareAtPrice(product) ?? resolveStorefrontSuppressionCompareAtPrice(storefrontProduct) ?? 0;
  }

  function resolveDirectCurrentCompareAtPrice(product) {
    return readFirstNumber([
      product?.compareAtPrice,
      product?.compare_at_price,
      product?.product?.compareAtPrice,
      product?.product?.compare_at_price,
    ]);
  }

  function resolveDirectSuppressionCompareAtPrice(product) {
    return readFirstNumber([
      product?.compareAtPriceMax,
      product?.compare_at_price_max,
      product?.compareAtPriceRange?.maxVariantPrice?.amount,
      product?.product?.compareAtPriceMax,
      product?.product?.compare_at_price_max,
      product?.product?.compareAtPriceRange?.maxVariantPrice?.amount,
      resolveDirectCurrentCompareAtPrice(product),
    ]);
  }

  function resolveStorefrontSuppressionCompareAtPrice(storefrontProduct) {
    return readFirstNumber([storefrontProduct?.compareAtPriceRange?.maxVariantPrice?.amount]);
  }

  function calculateSalePercentage(price, compareAtPrice) {
    return Math.ceil((price / compareAtPrice) * 100 - 100);
  }

  function buildSpecialBadge(badgeState, badgeConfig) {
    if (badgeState.available === false) {
      return {
        label: badgeConfig.soldOutLabel || DEFAULT_BADGE_CONFIG.soldOutLabel,
        colorClass: 'color-custom-badge-sold-out',
        isSpecial: true,
      };
    }

    if (!badgeState.hasSalePrice || badgeState.salePercentage === null) return null;

    return {
      label: `${badgeState.salePercentage}%`,
      colorClass: 'color-custom-badge-sale',
      isSpecial: true,
    };
  }

  function getThemeBadgeConfig() {
    if (themeBadgeConfig) return themeBadgeConfig;

    const configElement = document.getElementById(BADGE_CONFIG_ELEMENT_ID);
    if (!configElement) {
      themeBadgeConfig = DEFAULT_BADGE_CONFIG;
      return themeBadgeConfig;
    }

    const parsedConfig = safeJsonParse(configElement.textContent || '');
    themeBadgeConfig = {
      soldOutLabel: readString(parsedConfig?.soldOutLabel) || DEFAULT_BADGE_CONFIG.soldOutLabel,
    };

    return themeBadgeConfig;
  }

  function firstBoolean(values) {
    for (const value of values) {
      const normalizedValue = normalizeBoolean(value);
      if (normalizedValue !== null) return normalizedValue;
    }

    return null;
  }

  function normalizeBoolean(value) {
    if (typeof value === 'boolean') return value;
    if (value && typeof value === 'object' && typeof value.value === 'boolean') {
      return value.value;
    }

    const normalizedValue = readString(value).toLowerCase();
    if (!normalizedValue) return null;
    if (normalizedValue === 'false' || normalizedValue === '0' || normalizedValue === 'no') return false;
    if (normalizedValue === 'true' || normalizedValue === '1' || normalizedValue === 'yes') return true;

    return null;
  }

  function readFirstNumber(values) {
    for (const value of values) {
      const normalizedValue = toNumberOrNull(value);
      if (normalizedValue !== null) return normalizedValue;
    }

    return null;
  }

  function toNumber(value) {
    return toNumberOrNull(value) ?? 0;
  }

  function toNumberOrNull(value) {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }
    if (value && typeof value === 'object' && typeof value.amount !== 'undefined') {
      return toNumberOrNull(value.amount);
    }
    if (typeof value !== 'string') return null;

    const normalized = value.replace(/[^0-9.,-]/g, '').replace(',', '.');
    if (!normalized) return null;
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function normalizeBadge(badge) {
    if (!badge) return {};

    const rawValue = badge.value && typeof badge.value === 'object' ? badge.value : badge;
    const fieldMap = normalizeFieldMap(rawValue.fields || badge.fields);
    const label = readString(fieldMap.label) || readString(rawValue.label) || readString(rawValue.name);

    return {
      id: readString(rawValue.id) || readString(fieldMap.id),
      systemId: readString(rawValue.system?.id) || readString(fieldMap.system?.id) || readString(rawValue.systemId),
      active: toBoolean(fieldMap.active ?? rawValue.active, true),
      label,
      backgroundColor:
        readString(fieldMap.background_color) ||
        readString(rawValue.background_color) ||
        readString(rawValue.backgroundColor) ||
        '#ffffff',
      textColor:
        readString(fieldMap.text_color) ||
        readString(rawValue.text_color) ||
        readString(rawValue.textColor) ||
        '#171717',
    };
  }

  function normalizeFieldMap(fields) {
    if (Array.isArray(fields)) {
      return fields.reduce((mappedFields, field) => {
        if (field?.key) {
          mappedFields[field.key] = field.reference || field.value;
        }
        return mappedFields;
      }, {});
    }

    return fields && typeof fields === 'object' ? fields : {};
  }

  function toBoolean(value, fallback) {
    if (typeof value === 'boolean') return value;

    const normalizedValue = readString(value).toLowerCase();
    if (!normalizedValue) return fallback;
    if (normalizedValue === 'false' || normalizedValue === '0' || normalizedValue === 'no') return false;
    if (normalizedValue === 'true' || normalizedValue === '1' || normalizedValue === 'yes') return true;

    return fallback;
  }

  function readString(value) {
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number') return String(value);
    if (value && typeof value === 'object') {
      if (typeof value.value === 'string') return value.value.trim();
      if (typeof value.label === 'string') return value.label.trim();
      if (typeof value.id === 'string') return value.id.trim();
    }

    return '';
  }

  function isSuppressedNewBadge(badge, hasSalePrice) {
    if (!hasSalePrice) return false;

    return /^(neu|new)$/i.test(badge.label) || badge.systemId === NEW_BADGE_SYSTEM_ID;
  }

  function renderBadgeMarkup(badge) {
    const badgeClasses = ['rcc-product-badges__badge'];
    if (badge.isSpecial) {
      badgeClasses.push('rcc-product-badges__badge--special');
    }
    if (badge.colorClass) {
      badgeClasses.push(badge.colorClass);
    }

    const badgeStyle = renderBadgeStyle(badge);

    return `
      <span
        class="${escapeAttribute(badgeClasses.join(' '))}"
        ${badgeStyle ? `style="${badgeStyle}"` : ''}
      >${escapeHtml(badge.label)}</span>
    `;
  }

  function renderBadgeStyle(badge) {
    const styles = [];

    if (badge.backgroundColor) {
      styles.push(`--rcc-product-badges-bg: ${escapeAttribute(badge.backgroundColor)}`);
    }

    if (badge.textColor) {
      styles.push(`--rcc-product-badges-color: ${escapeAttribute(badge.textColor)}`);
    }

    return styles.join('; ');
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeAttribute(value) {
    return escapeHtml(value).replace(/`/g, '&#96;');
  }

  if (!customElements.get(COMPONENT_NAME)) {
    customElements.define(COMPONENT_NAME, ProductBadgesComponent);
  }
})();
